import { chartCssVars, useChart, useYScale } from "./chart-context";

/**
 * Straight reference line — vertical (`x`) or horizontal (`y`) — with an
 * optional label. Lodgeic extension for recharts `<ReferenceLine>` parity:
 * zero lines, "Today"/"Selected" dividers, and dashed target lines.
 *
 * `x` accepts a Date (time-series charts), a category string (bar charts),
 * or anything `new Date()` can parse. `y` is a value on the axis selected
 * by `yAxisId` (primary by default).
 */
export function ReferenceLine({
  x,
  y,
  yAxisId,
  stroke = chartCssVars.foregroundMuted,
  strokeWidth = 1,
  strokeDasharray = "4 4",
  opacity = 1,
  label,
  labelFill,
  labelFontSize = 10,
  labelFontFamily,
  labelPosition,
  labelDx = 0,
  labelDy = 0,
}) {
  const {
    xScale,
    yScale,
    innerWidth,
    innerHeight,
    barScale,
    bandWidth,
  } = useChart();
  const axisScale = useYScale(yAxisId) ?? yScale;

  const textProps = {
    fill: labelFill ?? stroke,
    fontSize: labelFontSize,
    fontFamily: labelFontFamily,
    style: { fontVariantNumeric: "tabular-nums" },
  };

  if (x != null) {
    let px = null;
    if (barScale && bandWidth != null && typeof x !== "object") {
      const bandPos = barScale(String(x));
      if (bandPos != null) {
        px = bandPos + bandWidth / 2;
      }
    }
    if (px == null && xScale) {
      const date = x instanceof Date ? x : new Date(x);
      if (!Number.isNaN(date.getTime())) {
        px = xScale(date);
      }
    }
    if (px == null || Number.isNaN(px)) {
      return null;
    }
    return (
      <g pointerEvents="none">
        <line
          opacity={opacity}
          stroke={stroke}
          strokeDasharray={strokeDasharray}
          strokeWidth={strokeWidth}
          x1={px}
          x2={px}
          y1={0}
          y2={innerHeight} />
        {label ? (
          <text
            {...textProps}
            textAnchor={labelPosition === "left" ? "end" : "middle"}
            x={px + labelDx + (labelPosition === "left" ? -6 : 0)}
            y={-6 + labelDy}>
            {label}
          </text>
        ) : null}
      </g>
    );
  }

  if (y != null && axisScale) {
    const py = axisScale(y);
    if (py == null || Number.isNaN(py)) {
      return null;
    }
    return (
      <g pointerEvents="none">
        <line
          opacity={opacity}
          stroke={stroke}
          strokeDasharray={strokeDasharray}
          strokeWidth={strokeWidth}
          x1={0}
          x2={innerWidth}
          y1={py}
          y2={py} />
        {label ? (
          <text
            {...textProps}
            textAnchor="end"
            x={innerWidth + labelDx}
            y={py - 5 + labelDy}>
            {label}
          </text>
        ) : null}
      </g>
    );
  }

  return null;
}

ReferenceLine.displayName = "ReferenceLine";

export default ReferenceLine;
